/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ScheduleItem from "./ScheduleItem";
import React from "react";
import {Package} from "types/entities/Package";
import {useTranslation} from "react-i18next";
import PackageItemGame from "./PackageItemGame";
import {uniquePackageWithActivityItem} from "utils/packageUtils";
import Loader from "components/Loader";
import {PackageItem} from "types/entities/PackageItem";
import clsx from "clsx";
import {DEFAULT_RESERVATION_ARRIVAL_MINUTES} from "helpers/config";
import ClockIcon from "@heroicons/react/24/outline/ClockIcon";

interface PackageDetailProps {
    packageDetail: Package;
}

interface PackageItemListProps {
    packageDetail: Package;
    packageItem: PackageItem;
    packageItemIndex: number;
}

const PackageItemGameList = (props: PackageItemListProps) => {
    const raceCount = [...Array(props.packageItem.article.race_count_total)];
    return (
        <div key={props.packageItem.id} className={"PackageItemGameList"}>
            {raceCount.map((el, gameIndex) => {
                return (
                    <div key={gameIndex} className={"RaceCountMap"}>
                        <PackageItemGame
                            key={gameIndex}
                            gameIndex={gameIndex}
                            packageDetail={props.packageDetail}
                            packageItem={props.packageItem}
                            packageItemIndex={props.packageItemIndex}
                            type={gameIndex >= 1 ? "game" : "activity"}
                        />
                    </div>
                );
            })}
        </div>
    );
};

const PackageDetailInfoDetailList = (props: PackageItemListProps) => {
    if (!props.packageItem.article) {
        return <Loader />;
    }

    return (
        <div key={props.packageItem.id} className={"PackageDetailInfoDetailList"}>
            {typeof props.packageItem.article.race_count_total !== undefined && props.packageItem.article.race_count_total > 1 ? (
                <PackageItemGameList packageDetail={props.packageDetail} packageItem={props.packageItem} packageItemIndex={props.packageItemIndex} />
            ) : (
                <PackageItemGame
                    key={props.packageItem.id}
                    gameIndex={0}
                    packageDetail={props.packageDetail}
                    packageItem={props.packageItem}
                    packageItemIndex={props.packageItemIndex}
                    type={"activity"}
                />
            )}
        </div>
    );
};

const PackageDetailInfoDetails = (props: PackageDetailProps) => {
    const {t} = useTranslation();
    return (
        <div className={clsx("PackageDetailInfoDetails py-4 sm:text-base text-gray-800")}>
            <div className={"flex flex-col justify-start items-start"}>
                <div className={"flex flex-row items-center justify-center mb-4"}>
                    <ClockIcon className={"w-6 h-6 object-cover mr-2"} />
                    <h5 className={"font-bold"}>{t("A typical schedule")}</h5>
                </div>
                <ul className={"flex flex-col justify-start pl-0 items-stretch space-y-4 divide-y divide-gray-500"}>
                    <ScheduleItem
                        duration={DEFAULT_RESERVATION_ARRIVAL_MINUTES}
                        label={t("Check-in")}
                        value={t("arrive before event time", {minutes: DEFAULT_RESERVATION_ARRIVAL_MINUTES})}
                    />
                    {uniquePackageWithActivityItem(props.packageDetail, true).map((packageItem, packageItemIndex) => {
                        return (
                            <PackageDetailInfoDetailList
                                key={packageItem.id}
                                packageDetail={props.packageDetail}
                                packageItem={packageItem}
                                packageItemIndex={packageItemIndex}
                            />
                        );
                    })}
                    {uniquePackageWithActivityItem(props.packageDetail, false).map((packageItem, packageItemIndex) => {
                        return (
                            <PackageDetailInfoDetailList
                                key={packageItem.id}
                                packageDetail={props.packageDetail}
                                packageItem={packageItem}
                                packageItemIndex={packageItemIndex}
                            />
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};

export default PackageDetailInfoDetails;
